import { pick } from 'lodash';
import { ALLOWED_SEARCH_PARAMS } from 'constants/search/allowedSearchParams';

/**
 * Filters an object to only include allowed search parameters.
 * This prevents unknown parameters (like gclid, utm_*, etc.) from breaking the search functionality.
 *
 * @param params - The parameters object to filter
 * @returns A new object containing only the allowed search parameters
 *
 * @example
 * const urlParams = { beds: '2', price: '500000', gclid: 'xyz123', utm_source: 'google' };
 * const filtered = filterAllowedSearchParams(urlParams);
 * // Result: { beds: '2', price: '500000' }
 */
export function filterAllowedSearchParams<T extends Record<string, any>>(
  params: T
): Partial<T> {
  if (!params || typeof params !== 'object') {
    return {};
  }

  // Convert ALLOWED_SEARCH_PARAMS to a regular array for lodash pick
  const allowedKeys = [...ALLOWED_SEARCH_PARAMS] as string[];

  // Use lodash pick to only keep allowed parameters
  return pick(params, allowedKeys) as Partial<T>;
}

type ShortTermRentFilters = {
  shortTermRent?: boolean;
  notShortTermRent?: boolean;
  shortTermRentTerms?: string[];
  notShortTermRentTerms?: string[];
};

/**
 * shortTermRent and notShortTermRent are opposite views of the same
 * building.allowsShortTerm field. The UI keeps them mutually exclusive, but a
 * hand-crafted or shared URL can set both, and the API applies each clause
 * independently — allowsShortTerm=true AND (false OR null) matches nothing, so
 * the page would silently return zero results. Drop both instead, along with the
 * frequency sub-filters that only make sense alongside them.
 *
 * @param values - Filter values to normalize
 * @returns The same object when there is no conflict, otherwise a normalized copy
 */
export function resolveShortTermRentConflict<T extends ShortTermRentFilters>(
  values: T
): T {
  if (!values?.shortTermRent || !values?.notShortTermRent) return values;

  return {
    ...values,
    shortTermRent: false,
    notShortTermRent: false,
    shortTermRentTerms: [],
    notShortTermRentTerms: [],
  };
}

/**
 * Sanitizes query parameters by removing both unknown parameters and internal tracking params.
 * This is used when preparing data to send to the server.
 *
 * @param params - The parameters object to sanitize
 * @param additionalParamsToOmit - Additional parameter keys to omit (optional)
 * @returns A new object with only valid search parameters
 */
export function sanitizeSearchParams<T extends Record<string, any>>(
  params: T,
  additionalParamsToOmit: string[] = []
): Partial<T> {
  // First filter to only allowed params
  const filtered = filterAllowedSearchParams(params);

  // Then remove any additional params that should be omitted
  if (additionalParamsToOmit.length > 0) {
    const result = { ...filtered };
    additionalParamsToOmit.forEach(key => {
      delete result[key];
    });
    return result;
  }

  return filtered;
}
